import type { TrainingDiagram } from '@ukinfo/shared';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import type { SvgPicture } from '../../lib/share-image';
import { PitchDiagram } from './PitchDiagram';

/**
 * Die Skizze als eigenstaendiger SVG-Text, so wie `PitchDiagram` sie zeichnet (mit Schrittnummern).
 *
 * Gezeichnet wird in einem losgeloesten Knoten und gleich wieder abgebaut. Das Ergebnis traegt alles
 * bei sich: Pfeilspitzen als `<marker>` in einem eigenen `<defs>` (die Kennung stammt aus `useId` und
 * ist je Zeichnung eindeutig), Farben als Attribute statt Klassen, keine Bilder und keine Schrift
 * aus dem Netz. Nur die Schriftfamilie wird auf Systemschriften festgelegt, denn in einem SVG, das als
 * Bild geladen wird, gelten weder die Schriften noch das Stylesheet der Seite.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Zeichenbreite, auf die das SVG als Bild aufgeloest wird (nur die Eigengroesse; gezeichnet wird skaliert). */
const SVG_PIXEL_WIDTH = 900;
const FONT_FAMILY = '-apple-system, \'Segoe UI\', Roboto, Helvetica, Arial, sans-serif';

export function sketchPicture(diagram: TrainingDiagram, title?: string): SvgPicture {
  const host = document.createElement('div');
  const root = createRoot(host);
  flushSync(() => root.render(<PitchDiagram diagram={diagram} title={title} showSteps />));
  const source = host.querySelector('svg');
  if (!source) {
    root.unmount();
    throw new Error('Die Skizze ließ sich nicht zeichnen');
  }

  const [, , vbWidth = 108, vbHeight = 108] = (source.getAttribute('viewBox') ?? '').split(/\s+/).map(Number);
  const width = SVG_PIXEL_WIDTH;
  const height = Math.round((SVG_PIXEL_WIDTH * vbHeight) / vbWidth);

  const svg = source.cloneNode(true) as SVGSVGElement;
  root.unmount();
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  // Klassen und Stil gehoeren der Seite; Beschreibung und Rolle braucht ein Bild nicht.
  for (const name of ['class', 'style', 'role', 'aria-label', 'data-pitch']) svg.removeAttribute(name);
  for (const element of svg.querySelectorAll('[font-family]')) element.setAttribute('font-family', FONT_FAMILY);

  // Der Serializer setzt den Namensraum selbst; sicherheitshalber nachholen, falls ein Browser es nicht tut (nie doppelt: sonst ist es kein XML).
  let text = new XMLSerializer().serializeToString(svg);
  if (!/^<svg[^>]*\sxmlns=/.test(text)) text = text.replace('<svg', `<svg xmlns="${SVG_NS}"`);
  return { svg: text, width, height };
}
